import React, { useState } from 'react';
import { Download, TrendingUp, BarChart3, PieChart, FileText, DollarSign } from 'lucide-react';
import { Button, SelectDropdown } from './Button';

export const ReportsAnalytics = () => {
    const [selectedPeriod, setSelectedPeriod] = useState('monthly');
    const [selectedReport, setSelectedReport] = useState('overview');

    const reportTypes = [
        { id: 'overview', label: 'Business Overview', icon: BarChart3 },
        { id: 'compliance', label: 'Compliance Report', icon: FileText },
        { id: 'profitability', label: 'Client Profitability', icon: DollarSign },
        { id: 'tax-analysis', label: 'Tax Analysis', icon: PieChart },
        { id: 'forecasting', label: 'Tax Forecasting', icon: TrendingUp }
    ];

    const complianceData = {
        gstr1Filed: 42,
        gstr3bFiled: 38,
        gstr1Pending: 3,
        gstr3bPending: 7,
        complianceRate: 89.2
    };

    const revenueData = {
        currentMonth: 450000,
        previousMonth: 380000,
        growth: 18.4,
        yearToDate: 4200000,
        projectedAnnual: 5400000
    };

    const clientProfitability = [
        { client: 'ABC Industries', revenue: 120000, hours: 45, profitMargin: 68 },
        { client: 'XYZ Corp', revenue: 85000, hours: 38, profitMargin: 72 },
        { client: 'Tech Solutions', revenue: 95000, hours: 42, profitMargin: 65 }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
                    <div className="flex space-x-3">
                        <SelectDropdown
                            value={selectedPeriod}
                            onChange={setSelectedPeriod}
                            options={[
                                { value: "weekly", label: "This Week" },
                                { value: "monthly", label: "This Month" },
                                { value: "quarterly", label: "This Quarter" },
                                { value: "yearly", label: "This Year" },
                                { value: "custom", label: "Custom Range" },
                            ]}
                        />
                        <Button label="Export Report" />

                    </div>
                </div>

                {/* Report Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {reportTypes.map((report) => (
                        <button
                            key={report.id}
                            onClick={() => setSelectedReport(report.id)}
                            className={`p-4 rounded-lg border-2 transition-colors ${selectedReport === report.id
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                }`}
                        >
                            <report.icon className="h-6 w-6 mx-auto mb-2" />
                            <span className="text-sm font-medium">{report.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                            <p className="text-2xl font-bold text-green-600">₹{(revenueData.currentMonth / 1000).toFixed(0)}K</p>
                            <div className="flex items-center mt-1">
                                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                                <span className="text-sm text-green-600">+{revenueData.growth}%</span>
                            </div>
                        </div>
                        <DollarSign className="h-8 w-8 text-green-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
                            <p className="text-2xl font-bold text-blue-600">{complianceData.complianceRate}%</p>
                            <p className="text-sm text-gray-500">This month</p>
                        </div>
                        <FileText className="h-8 w-8 text-blue-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Clients</p>
                            <p className="text-2xl font-bold text-purple-600">156</p>
                            <p className="text-sm text-gray-500">+5 this month</p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-purple-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Avg. Profit Margin</p>
                            <p className="text-2xl font-bold text-orange-600">68%</p>
                            <p className="text-sm text-gray-500">Across all clients</p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-orange-500" />
                    </div>
                </div>
            </div>

            {/* Detailed Reports */}
            {selectedReport === 'compliance' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filing Status</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">GSTR-1 Filed</span>
                                <div className="flex items-center space-x-2">
                                    <div className="w-32 bg-gray-200 rounded-full h-2">
                                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '93%' }}></div>
                                    </div>
                                    <span className="font-medium">{complianceData.gstr1Filed}/45</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">GSTR-3B Filed</span>
                                <div className="flex items-center space-x-2">
                                    <div className="w-32 bg-gray-200 rounded-full h-2">
                                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: '84%' }}></div>
                                    </div>
                                    <span className="font-medium">{complianceData.gstr3bFiled}/45</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Actions</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                <span className="text-red-800">GSTR-1 Pending</span>
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                                    {complianceData.gstr1Pending}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                                <span className="text-yellow-800">GSTR-3B Pending</span>
                                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-medium">
                                    {complianceData.gstr3bPending}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedReport === 'profitability' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Client Profitability Analysis</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Client
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Revenue
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Hours Spent
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Hourly Rate
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Profit Margin
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {clientProfitability.map((client, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {client.client}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{client.revenue.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {client.hours}h
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{Math.round(client.revenue / client.hours).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-green-600 h-2 rounded-full"
                                                        style={{ width: `${client.profitMargin}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-sm font-medium">{client.profitMargin}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {selectedReport === 'forecasting' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Liability Forecasting</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-medium text-blue-900">Current Quarter</h4>
                            <p className="text-2xl font-bold text-blue-600">₹2.4L</p>
                            <p className="text-sm text-blue-700">Estimated tax liability</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                            <h4 className="font-medium text-green-900">Next Quarter</h4>
                            <p className="text-2xl font-bold text-green-600">₹2.8L</p>
                            <p className="text-sm text-green-700">Projected increase</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4">
                            <h4 className="font-medium text-purple-900">Annual Projection</h4>
                            <p className="text-2xl font-bold text-purple-600">₹10.2L</p>
                            <p className="text-sm text-purple-700">Based on current trends</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Multi-year Comparison */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Multi-Year Comparison</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <p className="text-sm text-gray-600">2022</p>
                        <p className="text-xl font-bold text-gray-900">₹38L</p>
                        <p className="text-sm text-gray-500">Total Revenue</p>
                    </div>
                    <div className="text-center">
                        <p className="text-sm text-gray-600">2023</p>
                        <p className="text-xl font-bold text-gray-900">₹42L</p>
                        <p className="text-sm text-green-600">+10.5% growth</p>
                    </div>
                    <div className="text-center">
                        <p className="text-sm text-gray-600">2024 (Projected)</p>
                        <p className="text-xl font-bold text-gray-900">₹54L</p>
                        <p className="text-sm text-blue-600">+28.6% growth</p>
                    </div>
                </div>
            </div>
        </div>
    );
};